/**
 * Triton RPC Provider
 * Primary provider (Triton One Standard + Whirligig WebSockets)
 * WebSocket requires ping every 60 seconds (Whirligig)
 */

import { RPCProvider, HealthCheckResult, ProviderId, FAILOVER_CONFIG } from '../types';
import { debug } from "@/lib/debug";

export class TritonProvider implements RPCProvider {
  readonly id: ProviderId = 'triton';
  readonly name = 'Triton';
  readonly supportsWebhooks = false;

  private httpEndpoint: string;
  private wsEndpoint: string | undefined;
  private wsPingInterval: ReturnType<typeof setInterval> | null = null;
  private ws: WebSocket | null = null;

  constructor(httpEndpoint: string, wsEndpoint?: string) {
    this.httpEndpoint = httpEndpoint;
    this.wsEndpoint = wsEndpoint;
  }

  getHttpEndpoint(): string {
    return this.httpEndpoint;
  }

  getWsEndpoint(): string | undefined {
    return this.wsEndpoint;
  }

  /**
   * Start WebSocket keepalive ping for Triton Whirligig
   * Must be called when using the WS connection
   */
  startWsKeepalive(ws: WebSocket): void {
    this.ws = ws;
    
    // Clear any existing interval
    this.stopWsKeepalive();
    
    // Send ping every 55 seconds (Triton requires every 60s)
    this.wsPingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ jsonrpc: '2.0', method: 'ping' }));
        debug.trace('[TritonProvider] Sent WS keepalive ping');
      }
    }, FAILOVER_CONFIG.TRITON_WS_PING_INTERVAL_MS);
  }

  /**
   * Stop WebSocket keepalive ping
   */
  stopWsKeepalive(): void {
    if (this.wsPingInterval) {
      clearInterval(this.wsPingInterval);
      this.wsPingInterval = null;
    }
    this.ws = null;
  }

  async healthCheck(): Promise<HealthCheckResult> {
    const startTime = performance.now();
    
    try {
      const response = await fetch(this.httpEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 1,
          method: 'getHealth',
        }),
      });

      const latency = Math.round(performance.now() - startTime);

      if (!response.ok) {
        return {
          healthy: false,
          latency,
          timestamp: Date.now(),
          error: `HTTP ${response.status}`,
        };
      }

      const data = await response.json();
      const healthy = data.result === 'ok';

      return {
        healthy,
        latency,
        timestamp: Date.now(),
        error: healthy ? undefined : 'Node not healthy',
      };
    } catch (error) {
      const latency = Math.round(performance.now() - startTime);
      return {
        healthy: false,
        latency,
        timestamp: Date.now(),
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}
